import { RewardRedemptionStatus } from "@/stores/types";

export interface CommonQueryParams {
  limit: number;
  skip: number;
}

export interface CommonQueryParamsWithRegionId {
  limit: number;
  skip: number;
  regionId: string;
}

export interface PaginatedResponse<T> {
  limit: number;
  skip: number;
  total: number;
  items: T[];
}

export interface ApiResponse<T> {
  data: T;
  message?: string;
  success: boolean;
}

export enum VerificationTypes {
  VERIFY_ACCOUNT_CREATION = "VERIFY_ACCOUNT_CREATION", // * Use the primary attribute of the oranization.
  VERIFY_SECONDARY = "VERIFY_SECONDARY", // * Use the secondary attribute of the oranization.
}

export enum Channels {
  EMAIL = "EMAIL",
  MOBILE = "MOBILE",
  OTHER = "OTHER",
}

export interface RewardRedeemMetadata {
  claimLocationId: string;
}

export interface RewardRedeemRequest {
  rewardId: string;
  memberId: string;
  metadata: object;
  merchantLocationId: string;
}

export interface RewardRedeemResponse {
  success: boolean;
  message: string;
  redemptionId?: string;
}

export enum TransactionType {
  COLLECTION = "COLLECTION",
  REDEMPTION = "REDEMPTION",
  ADJUSTMENT = "ADJUSTMENT",
}

export enum TransactionStatus {
  VALID = "VALID",
  INVALID = "INVALID",
  PENDING = "PENDING",
}

export interface TransactionInvoiceData {
  invoiceId: string;
  invoiceDate: string;
  invoiceAmountWithTax: number;
  invoiceAmountWithoutTax: number;
  discountAmount: number;
  billAmount: number;
}

export interface TransactionProductItem {
  productId: string;
  productName: string;
  productCategory: string[];
  quantity: number;
  amount: number;
}

export enum OperationType {
  ADD = "ADD",
  SUBTRACT = "SUBTRACT",
}

export interface Transaction {
  _id: string;
  organizationId: string;
  regionId: string;
  merchantId: string;
  merchantLocationId: string;
  memberId: string;
  loyaltyId: string;
  cardId: string;
  transactionOn: string;
  redeemablePoints: number;
  tierPoints: number;
  transactionAmount: number;
  type: TransactionType;
  subType: string;
  importJobId: string;
  transactionSubType: {
    _id: string;
    name: string;
    operationType?: OperationType;
  };
  merchant: {
    merchantName: string;
  };
  merchantLocation: {
    locationName: string;
  };
  card: {
    cardNo: string;
  };
  productItems: Array<TransactionProductItem>;
  invoiceData: TransactionInvoiceData;
  pointRuleEvaluations: Array<{
    pointRuleId: string;
    points: number;
  }>;
  createdBy: string;
  updatedBy: string;
  status: TransactionStatus;
  notes?: string;
}

export interface Address {
  line1: string;
  line2?: string;
  line3?: string;
  city: string;
  stateOrProvince: string;
  zipOrPostcode: string;
}

export interface Identification {
  identificationType: "NATIONAL_ID";
  identificationNumber: string;
}

export interface NotificationPreference {
  preferredChannel: "EMAIL" | "MOBILE" | "EMAIL_AND_MOBILE";
  allowPromotionalNotifications: boolean;
}

export interface RewardMetadata {
  rewardId: string;
  refNumber: string;
  refName: string;
  notes: string;
}

export interface Card {
  cardNo: number;
  status: "ASSIGNED" | "UNASSIGNED";
}

export interface UpdateUserProfilePayload {
  firstName: string;
  lastName: string;
  preferredName: string;
  profilePicture: string;
  occupation: string;
  companyName: string;
  country: string;
  additionalPhoneNumbers: string[];
  birthDate: string;
  gender: Gender.MALE | Gender.FEMALE | Gender.OTHER;
  residentialAddress: {
    line1: string;
    line2: string;
    line3: string;
    city: string;
    stateOrProvince: string;
    zipOrPostcode: string;
  };
  postalAddress: {
    line1: string;
    line2: string;
    line3: string;
    city: string;
    stateOrProvince: string;
    zipOrPostcode: string;
  };
  notificationPreference: NotificationPreference;
  identifications: Identification[];
  rewardMetadata: [
    {
      rewardId: string;
      refNumber: string;
      refName: string;
      notes: string;
    }
  ];
  verifiedSecondaryChannelToken: string;
  secondaryChannel: Channels.EMAIL | Channels.MOBILE;
  secondaryChannelValue: string;
}

export interface UserProfile {
  _id: string;
  regionId: string;
  merchantLocationId: string;
  profilePicture?: string;
  identifications?: Identification[];
  firstName: string;
  lastName: string;
  preferredName?: string;
  mobileNumber?: string;
  countryCode: string;
  country: string;
  email?: string;
  birthDate: string;
  gender: Gender.MALE | Gender.FEMALE | Gender.OTHER;
  residentialAddress?: Address;
  points: number;
  tierPoints?: number;
  purchasesCount?: number;
  pointsToExpire: number;
  pointsExpireOn: string;
  affinityGroupId?: string;
  additionalPhoneNumbers?: string[];
  type: "PRIMARY" | "SECONDARY";
  notificationPreference: NotificationPreference;
  rewardMetadata?: RewardMetadata[];
  affinityGroup?: {
    name: string;
  };
  cards: Card[];
  status: string;
  merchantLocation?: {
    merchantId?: string;
    locationName?: string;
  };
  tier?: {
    tierId?: string;
    lastUpdatedOn?: string;
    tierJobId?: string;
  };
  tierData?: {
    name?: string;
    imageUrl?: string;
    benefits?: [];
  };
}

export interface RewardItem {
  _id: string;
  merchantId: string;
  name: string;
  description: string;
  type: "TANGIBLE" | "DIGITAL" | "SERVICE";
  subType: string;
  imageUrls: string[];
  pointValueType: "STATIC" | "DYNAMIC";
  pointsStatic: number;
  pointsBundles: Array<{
    points: number;
    bundleName: string;
    bundleValue: number;
  }>;
  validityPeriod: "FIXED" | "OPEN";
  validFrom: string;
  validTo: string;
  totalCount?: number;
  usedCount?: number;
  invalidatedCount?: number;
  dailyRedemptionLimit: "UNLIMITED" | "LIMITED";
  dailyRedemptionAmount: number;
  rewardMetadata: {
    allowAllClaimLocations: boolean;
    claimLocations: string[];
  };
  claimLocations: Array<{
    locationName: string;
    status: "DRAFT" | "PUBLISHED";
  }>;
  createdOn: string;
  shortDescription: string;
  menuIcon: string;
  redemptionSteps: Array<{
    step: number;
    details: string;
  }>;
}

export interface RewardItemsWithTotal {
  items: RewardItem[];
  total: number;
}

export enum RewardType {
  DIGITAL = "DIGITAL",
  TANGIBLE = "TANGIBLE",
  SERVICE = "SERVICE",
}

export enum RewardSubType {
  VOUCHER = "VOUCHER",
}

export enum ProcessingStatus {
  PENDING = "PENDING",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
}

export enum RefundStatus {
  NONE = "NONE",
  REFUNDED = "REFUNDED",
}

export interface RedemptionMetadata {
  partnerRefNumber: string;
  partnerRefName: string;
  partnerNotes: string;
  claimLocation: string;
}

export interface RedemptionReward {
  name: string;
  imageUrls: string[];
}

export interface RedemptionVoucher {
  voucherCode: string;
}

export interface RedemptionLocation {
  locationName: string;
}

export interface RedemptionHistoryEvent {
  eventDate: string;
  eventDetails: string;
  eventBy: string;
}

export interface RedemptionLog {
  _id: string;
  rewardId: string;
  distributionJobId: string;
  transactionId: string;
  pointsRedeemed: number;
  status: RewardRedemptionStatus;
  processingStatus: ProcessingStatus;
  rewardType: RewardType;
  rewardSubType: RewardSubType;
  refundStatus: RefundStatus;
  notes: string;
  metadata: RedemptionMetadata;
  reward: RedemptionReward;
  voucher?: RedemptionVoucher;
  pickupLocation: RedemptionLocation;
  historyEvents: RedemptionHistoryEvent[];
  createdOn: string;
}

export interface RedemptionLogsResponse {
  limit: number;
  skip: number;
  total: number;
  items: RedemptionLog[];
}

export interface RedemptionLogsWithTotal {
  items: RedemptionLog[];
  total: number;
}

export interface TransactionsResponse {
  limit: number;
  skip: number;
  total: number;
  items: Transaction[];
}

export interface TransactionsWithTotal {
  items: Transaction[];
  total: number;
}

// * Accounts
export interface RequestCreatePortalAccountVerifyTokenPayload {
  email?: string;
  mobileNumber?: string;
  cardNumber?: string;
}

export interface VerifyCreatePortalAccountRequestPayload {
  accountVerifyToken: string;
  otpCode: string;
  channel: Channels.EMAIL | Channels.MOBILE | Channels.OTHER;
  channelValue: string;
}

export interface CreatePortalAccountPayload {
  accountCreateToken: string;
  primaryChannel: Channels.EMAIL | Channels.MOBILE | Channels.OTHER;
  primaryChannelValue: string;
  regionId: string;
  merchantLocationId?: string; // TODO: REMOVE Add a task to use merchantLocationCode here instead.
  firstName: string;
  lastName: string;
  preferredName?: string;
  birthDate?: string;
  gender?: Gender;
  password: string;
  isExistingLoyaltyMember: boolean;
  memberId?: string;
}

export interface CreatePortalAccountParams {
  loginAfterSignup?: boolean;
  portalClientId?: string;
}

export interface RequestSecondaryChannelVerifyTokenPayload {
  email?: string;
  mobileNumber?: string;
  memberId?: string;
}

export interface VerifySecondaryChannelRequestPayload {
  secondaryChannelVerifyToken: string;
  otpCode: string;
  channel: Channels.EMAIL | Channels.MOBILE;
  channelToVerify: string;
}

export interface OTPRetryEligibilityData {
  nextEligibleTimestamp: string;
  waitSeconds: number;
  maxRetriesReached: boolean;
}

export interface RequestSecondaryChannelVerifyTokenResponse
  extends OTPRetryEligibilityData {
  secondaryChannelVerifyToken: string;
}
export interface VerifySecondaryChannelRequestResponse {
  verifiedSecondaryChannelToken: string;
}

export interface RequestCreatePortalAccountVerifyTokenResponse
  extends OTPRetryEligibilityData {
  accountVerifyToken: string;
}

export interface VerifyCreatePortalAccountRequestResponse {
  memberId: string;
  firstName: string;
  lastName: string;
  mobileNumber: string;
  email: string;
  merchantLocationId: string;
  cardNumber: string;
  username: string;
  accountCreateToken: string;
}

export interface KeycloakSessionResponse {
  access_token: string;
  expires_in?: number;
  refresh_expires_in?: number;
  refresh_token?: string;
  token_type: string;
  "not-before-policy"?: number;
  session_state?: string;
  scope?: string;
}

export interface LoginRequestPayload {
  username: string;
  password: string;
}

export interface CreatePortalAccountResponse {
  message: string;
  memberId: string;
  session?: KeycloakSessionResponse | null | undefined;
}

export interface LoginAfterSignupPayload {
  session?: KeycloakSessionResponse | null | undefined;
}

export interface Location {
  _id: string;
  locationName: string;
  isPickupLocation: boolean;
  contact: {
    address: Address;
  };
}
export interface TierData {
  _id: string;
  name: string;
  imageUrl: string;
  points?: number;
  orderCount?: number;
  benefits: [];
}

export interface PointRule {
  _id: string;
  name: string;
  status: string;
  ruleState: string;
  description: string;
  points: number;
  subType: string;
  merchantId: string;
  ruleData: {
    amountPerPoint: number;
  };
}

export enum Gender {
  MALE = "MALE",
  FEMALE = "FEMALE",
  OTHER = "OTHER",
}

export const GenderTypeOptions = [
  { value: Gender.MALE, label: "Male" },
  { value: Gender.FEMALE, label: "Female" },
  { value: Gender.OTHER, label: "Other" },
];

export interface LoyaltyErrorObject {
  response?: {
    data?: {
      error?: string;
      errorCode?: string;
    };
  };
}

export interface PublicRegion {
  _id: string;
  regionName: string;
  defaultCountryISO2Code: string;
  supportInfo: {
    phoneNumbers: [string];
    email: string;
    whatsappNumber: string;
  };
}

export interface PublicLocation {
  _id: string;
  locationName: string;
}

export enum MemberPrimaryAttribute {
  EMAIL = "EMAIL",
  MOBILE_NUMBER = "MOBILE_NUMBER",
}

export enum MemberPrimaryAttributeAsKeys {
  EMAIL = "email",
  MOBILE_NUMBER = "mobileNumber",
}

export interface OrganizationConfiguration {
  baseCountryCurrencyCode: string | undefined;
  tierConfiguration: {
    tierCalculationWindow: number;
  };
  portalConfiguration: {
    allowSelfSignup: boolean;
    showRewards: boolean;
    skipEmailVerification?: boolean; // Skip email verification when email is secondary attribute
  };
  memberPrimaryAttribute:
    | MemberPrimaryAttribute.EMAIL
    | MemberPrimaryAttribute.MOBILE_NUMBER;
}

export interface OrganizationPublicData {
  organizationName: string;
  organizationLogoImageUrl?: string;
  configuration: OrganizationConfiguration;
}

export interface PublicData {
  organization: OrganizationPublicData;
  regions: PublicRegion;
  locations: PublicLocation[];
}

export interface ExistingMember {
  memberId: string;
  firstName: string;
  lastName: string;
  mobileNumber: string;
  email: string;
  merchantLocationId: string;
  cardNumber: string;
  gender?: string;
  username: string;
}

export type RewardTabType = "all" | "my";

export enum RewardTabs {
  ALL = "all",
  MY = "my",
}

export interface Region {
  _id: string;
  regionName: string;
  defaultCountryISO2Code: string;
  defaultCurrencyCode: string;
  regionIconUrl: string;
  defaultMerchantId: string;
  defaultMerchantLocationId: string;
  pointConfiguration: {
    minPointRedemptionAmount: number;
    maxPointRedemptionAmount: number;
    minPointsBalanceForRedemption: number;
    pointExpiryMethod: "FIXED" | "ROLLING";
    pointExpiryStartMonth: number;
    pointExpiryEndMonth: number;
    pointExpiryPeriod: number;
    pointExpiryGracePeriod: number;
    currencyAmountPerPoint: number;
    regionalPointConversionRates: [
      {
        destinationRegionId: string;
        rate: number;
      }
    ];
    alias?: string;
    singularAlias?: string;
  };
  supportInfo: {
    phoneNumbers: [string];
    email: string;
    whatsappNumber: string;
  };
}
